import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import useBranchData from "layouts/tables/data/branchTable";

function Branches() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedBranch,
    setSelectedBranch,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddBranch,
  } = useBranchData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading branches...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Church Branches"
        subtitle="Manage the branch locations under your church."
        actions={
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddBranch}>
              New Branch
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search branches..."
        />
      </Card>

      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedBranch?.id ? "Edit Branch" : "Add New Branch"}
        onSave={handleEditSubmit}
        saveLabel={selectedBranch?.id ? "Save Changes" : "Create Branch"}
        maxWidth="sm"
      >
        <FormSection>
          <TextField
            label="Branch Name"
            value={selectedBranch?.branchName || ""}
            onChange={(e) => setSelectedBranch({ ...selectedBranch, branchName: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Branch Address"
            value={selectedBranch?.address || ""}
            onChange={(e) => setSelectedBranch({ ...selectedBranch, address: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Branch Phone"
            value={selectedBranch?.phone || ""}
            onChange={(e) => setSelectedBranch({ ...selectedBranch, phone: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            select
            label="Status"
            value={selectedBranch?.status || "active"}
            onChange={(e) => setSelectedBranch({ ...selectedBranch, status: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </FormSection>

        {/* Admin credentials — only shown when creating a new branch */}
        {!selectedBranch?.id && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ px: 0.5, mb: 0.5 }}>
              Branch Admin Account (optional)
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 0.5, display: "block", mb: 1 }}
            >
              Create a login for the branch administrator. They will have full access to the system
              filtered to this branch. Login credentials will be emailed to them.
            </Typography>
            <FormSection>
              <TextField
                label="Admin Full Name"
                value={selectedBranch?.adminName || ""}
                onChange={(e) =>
                  setSelectedBranch({ ...selectedBranch, adminName: e.target.value })
                }
                fullWidth
                margin="normal"
              />
              <TextField
                label="Admin Email"
                type="email"
                value={selectedBranch?.adminEmail || ""}
                onChange={(e) =>
                  setSelectedBranch({ ...selectedBranch, adminEmail: e.target.value })
                }
                fullWidth
                margin="normal"
                helperText="Login credentials will be sent here"
              />
              <TextField
                label="Admin Phone"
                value={selectedBranch?.adminPhone || ""}
                onChange={(e) =>
                  setSelectedBranch({ ...selectedBranch, adminPhone: e.target.value })
                }
                fullWidth
                margin="normal"
              />
              <TextField
                label="Admin Password"
                type="password"
                value={selectedBranch?.adminPassword || ""}
                onChange={(e) =>
                  setSelectedBranch({ ...selectedBranch, adminPassword: e.target.value })
                }
                fullWidth
                margin="normal"
                helperText="Leave blank to auto-generate a password"
              />
            </FormSection>
          </>
        )}
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedBranch?.branchName}" branch?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default Branches;
